import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmpresaSidebar } from "@/components/empresa/EmpresaSidebar";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Loader2, User, MapPin, Phone, Linkedin, Instagram, Briefcase, GraduationCap, Languages, Award, FileText, Calendar, Users, Accessibility, Filter, X, ChevronsUpDown, Check, Search, ChevronLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO, differenceInYears } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { isStartPlan, hasActiveCredits } from "@/lib/planUtils";
import { toast } from "@/hooks/use-toast";

// All available areas in the platform
const AREAS = ["Tecnologia da Informação", "Administrativo", "Comercial / Vendas", "Marketing", "Recursos Humanos", "Financeiro / Contábil", "Logística / Operações", "Saúde", "Educação", "Engenharia", "Atendimento ao Cliente", "Jurídico", "Produção / Industrial", "Outros"];

// Helper functions
const toTitleCase = (text: string): string => {
  return text.toLowerCase().split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};
const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};
const removeAccents = (text: string): string => {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};
type UF = {
  id: number;
  sigla: string;
  nome: string;
};
type City = {
  nome: string;
  codigo_ibge: string;
};
type Candidate = {
  id: string;
  user_id: string;
  full_name: string;
  phone: string | null;
  city: string | null;
  state: string | null;
  bio: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  desired_area: string | null;
  desired_job_type: string | null;
  desired_work_model: string | null;
  salary_expectation_min: number | null;
  salary_expectation_max: number | null;
  is_pcd: boolean | null;
  availability: string | null;
  soft_skills: string[] | null;
  gender: string | null;
  birth_date: string | null;
  desired_positions: string[] | null;
  
  marital_status: string | null;
  has_children_under_18: boolean | null;
};
type Experience = {
  id: string;
  company_name: string;
  role: string;
  start_date: string;
  end_date: string | null;
  description: string | null;
};
type Education = {
  id: string;
  course: string;
  institution: string;
  start_date: string;
  end_date: string | null;
};
type Skill = {
  id: string;
  skill_name: string;
  level: string | null;
};
type Language = {
  id: string;
  language: string;
  level: string;
};
type Course = {
  id: string;
  course_name: string;
  institution: string;
  start_date: string;
  end_date: string | null;
  workload_hours: number | null;
};
const EmpresaBancoTalentosPage = () => {
  const navigate = useNavigate();
  const {
    loading: accessLoading,
    companyProfile
  } = useCompanyAccess(true);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);

  // Redirect Start plan users without credits
  useEffect(() => {
    if (!accessLoading && companyProfile) {
      const isStart = isStartPlan(companyProfile.plan);
      const hasCredits = hasActiveCredits(
        companyProfile.credits,
        companyProfile.credits_expires_at
      );
      
      // Only redirect if Start plan AND no active credits
      if (isStart && !hasCredits) {
        toast({
          title: "Recurso não disponível",
          description: "O Banco de Talentos está disponível apenas para planos pagos ou usuários com créditos ativos.",
          variant: "destructive"
        });
        navigate("/empresa/dashboard");
      }
    }
  }, [accessLoading, companyProfile, navigate]);

  // Sheet state for mobile
  const [sheetOpen, setSheetOpen] = useState(false);

  // Filters
  const [filterState, setFilterState] = useState("all");
  const [filterCity, setFilterCity] = useState("");
  const [filterArea, setFilterArea] = useState("all");
  const [filterWorkModel, setFilterWorkModel] = useState("all");
  const [filterPcd, setFilterPcd] = useState("all");

  // Position search
  const [filterPosition, setFilterPosition] = useState("");

  // BrasilAPI states
  const [brazilianStates, setBrazilianStates] = useState<UF[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");

  // Candidate details
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [education, setEducation] = useState<Education[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Pagination states
  const CANDIDATES_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / CANDIDATES_PER_PAGE);


  // Fetch Brazilian states from BrasilAPI
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch("https://brasilapi.com.br/api/ibge/uf/v1");
        const data = await response.json();
        setBrazilianStates(data.sort((a: UF, b: UF) => a.sigla.localeCompare(b.sigla)));
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    fetchStates();
  }, []);

  // Fetch cities when state changes
  useEffect(() => {
    if (filterState && filterState !== "all") {
      const fetchCities = async () => {
        setLoadingCities(true);
        try {
          const response = await fetch(`https://brasilapi.com.br/api/ibge/municipios/v1/${filterState}`);
          const data = await response.json();
          setCities(data);
        } catch (error) {
          console.error("Error fetching cities:", error);
          setCities([]);
        }
        setLoadingCities(false);
      };
      fetchCities();
    } else {
      setCities([]);
      setFilterCity("");
    }
  }, [filterState]);

  // Fetch candidates with server-side pagination and filters
  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    
    const from = (currentPage - 1) * CANDIDATES_PER_PAGE;
    const to = from + CANDIDATES_PER_PAGE - 1;

    let queryBuilder = supabase
      .from("candidate_profiles")
      .select("*", { count: 'exact' })
      .eq("is_active", true);

    // Apply state filter
    if (filterState !== "all") {
      queryBuilder = queryBuilder.eq("state", filterState);
    }

    // Apply city filter
    if (filterCity) {
      queryBuilder = queryBuilder.ilike("city", filterCity);
    }

    // Apply area filter
    if (filterArea !== "all") {
      queryBuilder = queryBuilder.eq("desired_area", filterArea);
    }

    // Apply work model filter
    if (filterWorkModel !== "all") {
      queryBuilder = queryBuilder.ilike("desired_work_model", `%${filterWorkModel}%`);
    }

    // Apply PCD filter
    if (filterPcd === "yes") {
      queryBuilder = queryBuilder.eq("is_pcd", true);
    } else if (filterPcd === "no") {
      queryBuilder = queryBuilder.eq("is_pcd", false);
    }

    // Apply position filter (desired_positions is an array)
    if (filterPosition.trim()) {
      queryBuilder = queryBuilder.contains("desired_positions", [filterPosition.trim()]);
    }

    const { data, error, count } = await queryBuilder
      .order("full_name", { ascending: true })
      .range(from, to);

    if (!error && data) {
      setCandidates(data);
      setTotalCount(count || 0);
    }
    
    setLoading(false);
  }, [currentPage, filterState, filterCity, filterArea, filterWorkModel, filterPcd, filterPosition]);

  // Effect to call fetch when dependencies change
  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Fetch candidate details when selected
  useEffect(() => {
    if (!selectedCandidate) return;
    const fetchCandidateDetails = async () => {
      setLoadingDetails(true);
      const candidateId = selectedCandidate.id;
      const [expRes, eduRes, skillsRes, langRes, coursesRes] = await Promise.all([
        supabase.from("candidate_experiences").select("*").eq("candidate_id", candidateId).order("start_date", { ascending: false }),
        supabase.from("candidate_education").select("*").eq("candidate_id", candidateId).order("start_date", { ascending: false }),
        supabase.from("candidate_skills").select("*").eq("candidate_id", candidateId),
        supabase.from("candidate_languages").select("*").eq("candidate_id", candidateId),
        supabase.from("candidate_courses").select("*").eq("candidate_id", candidateId).order("start_date", { ascending: false })
      ]);
      setExperiences(expRes.data || []);
      setEducation(eduRes.data || []);
      setSkills(skillsRes.data || []);
      setLanguages(langRes.data || []);
      setCourses(coursesRes.data || []);
      setLoadingDetails(false);
    };
    fetchCandidateDetails();
  }, [selectedCandidate]);


  // Filtered cities for autocomplete
  const filteredCities = (() => {
    if (!citySearch) return cities.slice(0, 50);
    const searchLower = removeAccents(citySearch.toLowerCase());
    return cities.filter(city => removeAccents(city.nome.toLowerCase()).includes(searchLower)).slice(0, 50);
  })();

  const clearFilters = () => {
    setFilterPosition("");
    setFilterCity("");
    setFilterState("all");
    setFilterArea("all");
    setFilterWorkModel("all");
    setFilterPcd("all");
    setCitySearch("");
    setCurrentPage(1);
  };
  
  const hasActiveFilters = filterPosition || filterCity || filterState !== "all" || filterArea !== "all" || filterWorkModel !== "all" || filterPcd !== "all";
  
  const handleStateChange = (value: string) => {
    setFilterState(value);
    setFilterCity("");
    setCitySearch("");
    setCurrentPage(1);
  };

  // Pagination functions
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generatePageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("ellipsis");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    
    return pages;
  };
  const handleCandidateClick = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    // Open sheet on mobile/tablet (< 768px)
    if (window.innerWidth < 768) {
      setSheetOpen(true);
    }
  };
  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "MMM yyyy", {
        locale: ptBR
      });
    } catch {
      return dateStr;
    }
  };
  const calculateAge = (birthDate: string) => {
    try {
      return differenceInYears(new Date(), parseISO(birthDate));
    } catch {
      return null;
    }
  };
  const formatGender = (gender: string | null) => {
    if (!gender) return null;
    const genderMap: Record<string, string> = {
      masculino: "Masculino",
      feminino: "Feminino",
      outro: "Outro",
      prefiro_nao_informar: "Prefere não informar"
    };
    return genderMap[gender.toLowerCase()] || gender;
  };


  const formatMaritalStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      solteiro: "Solteiro(a)",
      casado: "Casado(a)",
      divorciado: "Divorciado(a)",
      viuvo: "Viúvo(a)",
      uniao_estavel: "União Estável"
    };
    return statusMap[status.toLowerCase()] || status;
  };

  // Timeline Item Component
  const TimelineItem = ({ 
    title, 
    subtitle, 
    period, 
    description, 
    isLast 
  }: { 
    title: string;
    subtitle: string;
    period: string;
    description?: string | null;
    isLast?: boolean;
  }) => (
    <div className="relative pl-6">
      {/* Vertical line */}
      {!isLast && (
        <div className="absolute left-[7px] top-4 h-full w-[2px] bg-border" />
      )}
      {/* Timeline point */}
      <div className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-primary bg-background" />
      {/* Content */}
      <div className="pb-5">
        <p className="font-medium text-sm">{title}</p>
        <p className="text-muted-foreground text-sm">{subtitle}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{period}</p>
        {description && (
          <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
  );

  // Candidate Details Component (reusable for both inline and modal)
  const CandidateDetails = ({
    candidate
  }: {
    candidate: Candidate;
  }) => <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold">
          {candidate.full_name}
        </h2>
        {/* Personal Info Row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
          {candidate.phone && <span className="flex items-center gap-1">
              <Phone className="h-3.5 w-3.5" />
              {formatPhone(candidate.phone)}
            </span>}
          {candidate.city && <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {candidate.city}
              {candidate.state && `, ${candidate.state}`}
            </span>}
          {candidate.gender && <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {formatGender(candidate.gender)}
            </span>}
          {candidate.birth_date && <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {calculateAge(candidate.birth_date)} anos
            </span>}
        </div>
        {/* Links Row */}
        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
          {candidate.linkedin_url && <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
              <Linkedin className="h-3.5 w-3.5" />
              LinkedIn
            </a>}
          {candidate.github_url && <a href={candidate.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:underline text-pink-500">
              <Instagram className="h-3.5 w-3.5" />
              Instagram
            </a>}
        </div>
      </div>

      <Separator />

      {/* Bio */}
      {candidate.bio && <>
          <div>
            <h3 className="font-semibold mb-2">Sobre</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {candidate.bio}
            </p>
          </div>
          <Separator />
        </>}

      {/* Professional Goals */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          Objetivos Profissionais
        </h3>
        {!candidate.desired_area && 
         !candidate.desired_job_type && 
         !candidate.desired_work_model && 
         !candidate.salary_expectation_min && 
         !candidate.salary_expectation_max &&
         (!candidate.desired_positions || candidate.desired_positions.length === 0) ? (
          <p className="text-sm text-muted-foreground">Nenhum objetivo cadastrado</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {candidate.desired_area && <div>
                  <p className="text-muted-foreground">Área desejada</p>
                  <p className="font-medium">{candidate.desired_area}</p>
                </div>}
              {candidate.desired_job_type && <div>
                  <p className="text-muted-foreground">Tipo de vaga</p>
                  <p className="font-medium">{candidate.desired_job_type}</p>
                </div>}
              {candidate.desired_work_model && <div>
                  <p className="text-muted-foreground">Modelo de trabalho</p>
                  <p className="font-medium">{candidate.desired_work_model}</p>
                </div>}
              {(candidate.salary_expectation_min || candidate.salary_expectation_max) && <div>
                  <p className="text-muted-foreground">Pretensão salarial</p>
                  <p className="font-medium">
                    {candidate.salary_expectation_min && candidate.salary_expectation_max ? `R$ ${candidate.salary_expectation_min.toLocaleString()} - R$ ${candidate.salary_expectation_max.toLocaleString()}` : candidate.salary_expectation_min ? `A partir de R$ ${candidate.salary_expectation_min.toLocaleString()}` : `Até R$ ${candidate.salary_expectation_max?.toLocaleString()}`}
                  </p>
                </div>}
            </div>
            {/* Cargos Pretendidos */}
            {candidate.desired_positions && candidate.desired_positions.length > 0 && (
              <div className="mt-4">
                <p className="text-muted-foreground text-sm mb-2">Cargos pretendidos</p>
                <div className="flex flex-wrap gap-2">
                  {candidate.desired_positions.map((pos, idx) => (
                    <Badge key={idx} variant="secondary">{pos}</Badge>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Separator />

      {/* Experiences */}
      <div>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          Experiências
        </h3>
        {experiences.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma experiência cadastrada</p>
        ) : (
          <div className="ml-2">
            {experiences.map((exp, index) => (
              <TimelineItem
                key={exp.id}
                title={exp.role}
                subtitle={exp.company_name}
                period={`${formatDate(exp.start_date)} - ${exp.end_date ? formatDate(exp.end_date) : "Atual"}`}
                description={exp.description}
                isLast={index === experiences.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* Cursos */}
      <div>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Award className="h-4 w-4" />
          Cursos
        </h3>
        {courses.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum curso cadastrado</p>
        ) : (
          <div className="ml-2">
            {courses.map((course, index) => (
              <TimelineItem
                key={course.id}
                title={course.course_name}
                subtitle={course.institution}
                period={`${formatDate(course.start_date)} - ${course.end_date ? formatDate(course.end_date) : "Em andamento"}${course.workload_hours ? ` • ${course.workload_hours}h` : ""}`}
                isLast={index === courses.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* Education */}
      <div>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <GraduationCap className="h-4 w-4" />
          Formação
        </h3>
        {education.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma formação cadastrada</p>
        ) : (
          <div className="ml-2">
            {education.map((edu, index) => (
              <TimelineItem
                key={edu.id}
                title={edu.course}
                subtitle={edu.institution}
                period={`${formatDate(edu.start_date)} - ${edu.end_date ? formatDate(edu.end_date) : "Cursando"}`}
                isLast={index === education.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* Skills */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Award className="h-4 w-4" />
          Habilidades
        </h3>
        {skills.length === 0 && (!candidate.soft_skills || candidate.soft_skills.length === 0) ? <p className="text-sm text-muted-foreground">Nenhuma habilidade cadastrada</p> : <div className="space-y-3">
            {skills.length > 0 && <div>
                <p className="text-xs text-muted-foreground mb-2">Competências técnicas</p>
                <div className="flex flex-wrap gap-2">
                  {skills.map(skill => <Badge key={skill.id} variant="secondary">
                      {skill.skill_name}
                      {skill.level && ` • ${skill.level}`}
                    </Badge>)}
                </div>
              </div>}
            {candidate.soft_skills && candidate.soft_skills.length > 0 && <div>
                <p className="text-xs text-muted-foreground mb-2">Competências comportamentais</p>
                <div className="flex flex-wrap gap-2">
                  {candidate.soft_skills.map((skill, idx) => <Badge key={idx} variant="outline">
                      {skill}
                    </Badge>)}
                </div>
              </div>}
          </div>}
      </div>

      <Separator />

      {/* Languages */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Languages className="h-4 w-4" />
          Idiomas
        </h3>
        {languages.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum idioma cadastrado</p> : <div className="flex flex-wrap gap-2">
            {languages.map(lang => <Badge key={lang.id} variant="secondary">
                {lang.language} • {lang.level}
              </Badge>)}
          </div>}
      </div>

      {/* Dados Adicionais */}
      <Separator />
      <div>
        <h3 className="font-semibold mb-3">Dados Adicionais</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {candidate.marital_status && <div>
              <p className="text-muted-foreground">Estado civil</p>
              <p className="font-medium">{formatMaritalStatus(candidate.marital_status)}</p>
            </div>}
          {candidate.has_children_under_18 !== null && <div>
              <p className="text-muted-foreground">Filhos menores de 18 anos</p>
              <p className="font-medium">{candidate.has_children_under_18 ? "Sim" : "Não"}</p>
            </div>}
          {candidate.is_pcd && <div className="flex items-center gap-2">
              <Accessibility className="h-4 w-4 text-primary" />
              <span>Pessoa com Deficiência (PCD)</span>
            </div>}
          {candidate.availability && <div>
              <p className="text-muted-foreground">Disponibilidade</p>
              <p className="font-medium">{candidate.availability}</p>
            </div>}
        </div>
      </div>
    </div>;
  if (accessLoading || loading) {
    return <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center bg-muted/30">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>;
  }
  return <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 flex">
        <EmpresaSidebar />
        <main className="flex-1 p-4 md:p-6 bg-muted/30 overflow-hidden">
          <div className="h-full flex flex-col mobile-container max-w-7xl md:mx-auto">
            {/* Header - with padding for mobile sidebar button */}
            <div className="mb-4 lg:pl-0 pt-10 md:pt-0 flex-col gap-0 flex items-start justify-between py-0 px-0 pl-0">
              <div>
                <h1 className="text-2xl font-display font-bold">Banco de Talentos</h1>
                <p className="text-muted-foreground">
                  {loading 
                    ? "Carregando..." 
                    : totalCount === 0
                      ? "Nenhum candidato encontrado"
                      : `Mostrando ${((currentPage - 1) * CANDIDATES_PER_PAGE) + 1}-${Math.min(currentPage * CANDIDATES_PER_PAGE, totalCount)} de ${totalCount} candidato${totalCount !== 1 ? "s" : ""}`
                  }
                </p>
              </div>
            </div>

            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4 pl-12 md:pl-4 px-[16px]">
                <div className="flex items-center gap-2 mb-3">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filtros</span>
                  {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-xs">
                      <X className="h-3 w-3 mr-1" />
                      Limpar filtros
                    </Button>}
                </div>
                
                {/* Position Search */}
                <div className="mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Buscar pelo cargo (ex: Analista, Desenvolvedor, Vendedor...)" value={filterPosition} onChange={e => { setFilterPosition(e.target.value); setCurrentPage(1); }} className="pl-9" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Busca nos cargos pretendidos pelo candidato
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {/* State Filter */}
                  <Select value={filterState} onValueChange={handleStateChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os estados</SelectItem>
                      {brazilianStates.map(state => <SelectItem key={state.sigla} value={state.sigla}>{state.sigla} - {state.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  {/* City Filter with Autocomplete */}
                  <Popover open={cityOpen} onOpenChange={setCityOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={cityOpen} className="w-full justify-between font-normal" disabled={filterState === "all" || loadingCities}>
                        {loadingCities ? <span className="text-muted-foreground truncate">Carregando...</span> : filterCity ? <span className="truncate">{toTitleCase(filterCity)}</span> : <span className="text-muted-foreground truncate">
                            {filterState === "all" ? "Selecione o estado primeiro" : "Selecione a cidade"}
                          </span>}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0">
                      <Command shouldFilter={false}>
                        <CommandInput placeholder="Buscar cidade..." value={citySearch} onValueChange={setCitySearch} />
                        <CommandList>
                          <CommandEmpty>Nenhuma cidade encontrada</CommandEmpty>
                          <CommandGroup>
                            {filterCity && <CommandItem value="clear" onSelect={() => {
                            setFilterCity("");
                            setCitySearch("");
                            setCityOpen(false);
                          }}>
                                <X className="mr-2 h-4 w-4" />
                                Limpar seleção
                              </CommandItem>}
                            {filteredCities.map(city => <CommandItem key={city.codigo_ibge} value={city.nome} onSelect={() => {
                            setFilterCity(toTitleCase(city.nome));
                            setCitySearch("");
                            setCityOpen(false);
                          }}>
                                <Check className={cn("mr-2 h-4 w-4", filterCity.toLowerCase() === city.nome.toLowerCase() ? "opacity-100" : "opacity-0")} />
                                {toTitleCase(city.nome)}
                              </CommandItem>)}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {/* Area Filter */}
                  <Select value={filterArea} onValueChange={(v) => { setFilterArea(v); setCurrentPage(1); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Área" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as áreas</SelectItem>
                      {AREAS.map(area => <SelectItem key={area} value={area}>{area}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  {/* Work Model Filter */}
                  <Select value={filterWorkModel} onValueChange={(v) => { setFilterWorkModel(v); setCurrentPage(1); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os modelos</SelectItem>
                      <SelectItem value="Presencial">Presencial</SelectItem>
                      <SelectItem value="Híbrido">Híbrido</SelectItem>
                      <SelectItem value="Remoto">Remoto</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* PCD Filter */}
                  <Select value={filterPcd} onValueChange={(v) => { setFilterPcd(v); setCurrentPage(1); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="PCD" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">PCD</SelectItem>
                      <SelectItem value="no">Não PCD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Mobile Layout - Full width list */}
            <div className="md:hidden flex-1 flex flex-col overflow-hidden">
              <Card className="flex-1 flex flex-col overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Candidatos ({totalCount})</CardTitle>
                </CardHeader>
                <ScrollArea className="flex-1">
                  <div className="px-4 pb-4 space-y-3 overflow-hidden">
                    {candidates.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhum candidato encontrado</p>
                        {hasActiveFilters && (
                          <Button variant="link" onClick={clearFilters} className="mt-2">
                            Limpar filtros
                          </Button>
                        )}
                      </div>
                    ) : (
                      candidates.map((candidate) => (
                        <Card
                          key={candidate.id}
                          onClick={() => handleCandidateClick(candidate)}
                          className={`p-4 cursor-pointer transition-all ${
                            selectedCandidate?.id === candidate.id
                              ? "ring-2 ring-primary bg-primary/5"
                              : "hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-base truncate">{candidate.full_name}</p>
                              {candidate.city && (
                                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                  <MapPin className="h-3.5 w-3.5" />
                                  {candidate.city}
                                  {candidate.state && `, ${candidate.state}`}
                                </p>
                              )}
                              {candidate.desired_area && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {candidate.desired_area}
                                </p>
                              )}
                            </div>
                            {candidate.is_pcd && (
                              <Badge variant="outline" className="text-xs shrink-0">PCD</Badge>
                            )}
                          </div>
                        </Card>
                      ))
                    )}
                  </div>
                </ScrollArea>
                {/* Pagination Mobile */}
                {totalCount > 0 && (
                  <div className="px-4 pb-4 pt-2 border-t">
                    <Pagination>
                      <PaginationContent className="flex-wrap">
                        <PaginationItem>
                          <PaginationPrevious 
                            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                            className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                        
                        {generatePageNumbers().map((pageNum, idx) => (
                          pageNum === "ellipsis" ? (
                            <PaginationItem key={`ellipsis-${idx}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          ) : (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                onClick={() => handlePageChange(pageNum)}
                                isActive={currentPage === pageNum}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        ))}
                        
                        <PaginationItem>
                          <PaginationNext 
                            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </Card>

              {/* Sheet for candidate details on mobile */}
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent side="right" className="w-full sm:w-[95vw] p-0 overflow-y-auto">
                  <SheetHeader className="p-4 border-b sticky top-0 bg-background z-10">
                    <div className="flex items-center gap-3">
                      <Button variant="ghost" size="icon" onClick={() => setSheetOpen(false)}>
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <SheetTitle className="text-left">Detalhes do Candidato</SheetTitle>
                    </div>
                  </SheetHeader>
                  <div className="p-4">
                    {selectedCandidate && !loadingDetails ? (
                      <CandidateDetails candidate={selectedCandidate} />
                    ) : loadingDetails ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : null}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop Layout - Split View */}
            <div className="hidden md:flex flex-1 gap-4 overflow-hidden">
              {/* Left Panel - Candidates List (45%) */}
              <Card className="w-[45%] lg:w-2/5 flex flex-col overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Candidatos ({totalCount})</CardTitle>
                </CardHeader>
                <ScrollArea className="flex-1">
                  <div className="px-4 pb-4 space-y-2 overflow-hidden">
                    {candidates.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhum candidato encontrado</p>
                        {hasActiveFilters && (
                          <Button variant="link" onClick={clearFilters} className="mt-2">
                            Limpar filtros
                          </Button>
                        )}
                      </div>
                    ) : (
                      candidates.map((candidate) => (
                        <div
                          key={candidate.id}
                          onClick={() => handleCandidateClick(candidate)}
                          className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                            selectedCandidate?.id === candidate.id
                              ? "bg-primary/10 border-primary"
                              : "hover:bg-muted border-transparent"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate">{candidate.full_name}</p>
                              {candidate.city && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                  <MapPin className="h-3 w-3" />
                                  {candidate.city}
                                  {candidate.state && `, ${candidate.state}`}
                                </p>
                              )}
                              {candidate.desired_area && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {candidate.desired_area}
                                </p>
                              )}
                            </div>
                            {candidate.is_pcd && (
                              <Badge variant="outline" className="text-xs">PCD</Badge>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
                {/* Pagination Desktop */}
                {totalCount > 0 && (
                  <div className="px-4 pb-4 pt-2 border-t">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                            className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                        
                        {generatePageNumbers().map((pageNum, idx) => (
                          pageNum === "ellipsis" ? (
                            <PaginationItem key={`ellipsis-${idx}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          ) : (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                onClick={() => handlePageChange(pageNum)}
                                isActive={currentPage === pageNum}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        ))}
                        
                        <PaginationItem>
                          <PaginationNext 
                            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </Card>

              {/* Right Panel - Candidate Details (55%) */}
              <Card className="w-[55%] lg:w-3/5 flex flex-col overflow-hidden">
                {!selectedCandidate ? (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
                      <p>Selecione um candidato para ver o currículo</p>
                    </div>
                  </div>
                ) : loadingDetails ? (
                  <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <ScrollArea className="flex-1">
                    <div className="p-6">
                      <CandidateDetails candidate={selectedCandidate} />
                    </div>
                  </ScrollArea>
                )}
              </Card>
            </div>
          </div>
        </main>
      </div>


      <Footer />
    </div>;
};
export default EmpresaBancoTalentosPage;